/*
 * Copyright (c) 2024. Bluemate B.V.
 */

import {ResponseData} from '@dvrd/dvr-controls';
import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import DefaultEmail from '../../../models/defaultEmailModel';
import {EmailFlow} from '../../../util/interfaces';
import {ADMIN_TEMPLATE_EDITOR_ROUTE} from '../../../util/constants';
import DefaultEmails from './defaultEmails';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

const LIMIT = 10;
type LoadConfig = {
    page?: number;
    search?: string;
    enabled?: boolean | null;
    emailFlows?: Array<EmailFlow>;
};

export default function DefaultEmailsController() {
    const {t} = useTranslation();
    const [defaultEmails, setDefaultEmails] = useState<Array<DefaultEmail>>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [emailFlows, setEmailFlows] = useState<Array<EmailFlow>>([EmailFlow.INVOICE, EmailFlow.REVIEW_INVOICE]);
    const [enabled, setEnabled] = useState<boolean | null>(null);
    const [page, setPage] = useState(0);
    const navigate = useNavigate();

    function onChangeSearch(value: string) {
        setSearch(value);
    }

    function onEnterSearch() {
        loadDefaultEmails();
    }

    function onChangeEmailFlow(flow: EmailFlow) {
        return function () {
            const _flows = emailFlows.slice();
            if (_flows.includes(flow)) _flows.splice(_flows.indexOf(flow), 1);
            else _flows.push(flow);
            setEmailFlows(_flows);
            setPage(0);
            loadDefaultEmails({emailFlows: _flows, page: 0});
        };
    }

    function onChangeEnabled(value: boolean) {
        return function () {
            const next = value === enabled ? null : value;
            setEnabled(next);
            setPage(0);
            loadDefaultEmails({enabled: next, page: 0});
        };
    }

    function onChangePage(nextPage: number) {
        return function () {
            setPage(nextPage);
            loadDefaultEmails({page: nextPage});
        };
    }

    function onClickTemplate(email: DefaultEmail) {
        return function () {
            navigate(ADMIN_TEMPLATE_EDITOR_ROUTE(email.id));
        };
    }

    function onClickNew() {
        navigate(ADMIN_TEMPLATE_EDITOR_ROUTE(null));
    }

    function onPreviewError(email: DefaultEmail) {
        return function () {
            const emails = defaultEmails.slice();
            for (const defaultEmail of emails) {
                if (defaultEmail.id !== email.id) continue;
                defaultEmail.previewURLError = true;
                break;
            }
            setDefaultEmails(emails);
        };
    }

    function loadDefaultEmails(config?: LoadConfig) {
        setLoading(true);
        const _page = config?.page ?? page;
        const _search = config?.search ?? search;
        const _emailFlows = config?.emailFlows === undefined ? emailFlows : config.emailFlows;
        const _enabled = config?.enabled === undefined ? enabled : config.enabled;
        DefaultEmail.getAllAdmin({
            limit: LIMIT,
            enabled: _enabled,
            page: _page,
            flows: _emailFlows,
            search: _search,
            callback: (defaultEmails: Array<DefaultEmail>, success: boolean, data: ResponseData) => {
                setLoading(false);
                if (success) {
                    if (_page === 0) setTotal(data.total);
                    setDefaultEmails(defaultEmails);
                } else
                    showDialog(
                        data.message ?? t('admin.defaultEmails.loadFailedMsg', 'Het laden is niet gelukt.'),
                        t('admin.defaultEmails.loadFailedTitle', 'Laden mislukt')
                    );
            }
        });
    }

    useEffect(() => {
        loadDefaultEmails();
    }, []);

    return (
        <DefaultEmails
            onChangeSearch={onChangeSearch}
            onEnterSearch={onEnterSearch}
            onChangeEmailFlow={onChangeEmailFlow}
            onChangeEnabled={onChangeEnabled}
            onChangePage={onChangePage}
            onClickTemplate={onClickTemplate}
            onClickNew={onClickNew}
            onPreviewError={onPreviewError}
            defaultEmails={defaultEmails}
            total={total}
            loading={loading}
            search={search}
            emailFlows={emailFlows}
            enabled={enabled}
            page={page}
            limit={LIMIT}
        />
    );
}
